import {AvailableFilter, SelectedFilter} from '@/components/base/filters/types';
import i18n from '@/i18n';
import router from '@/router';
import {useWorkspaceStore} from '@/stores/workspace';
import {formatError} from '@/utils';
import {
  CheckoutExternalStore,
  CheckoutResponse,
  GetManyOrderQuery,
  OrderDraftResponse,
} from '@winelivery-org/wl-client-sdk-js/dist/models/apis/orders';
import {defineStore} from 'pinia';
import {useAppStore} from './app';
import sdk from '@/plugins/wl-client';

const ORDER_DRAFTS_STORE_ID = 'orderDrafts';

export type OrderDraftFilterKeys = 'store' | 'fromDate' | 'toDate';

const FILTER_KEYS: OrderDraftFilterKeys[] = ['store', 'fromDate', 'toDate'];

interface State {
  filters: GetManyOrderQuery;
  availableFilters: AvailableFilter<OrderDraftFilterKeys>[];
  selectedFilters: SelectedFilter<OrderDraftFilterKeys>[];
  drafts: OrderDraftResponse[];
  draft?: OrderDraftResponse;
  count: number;
}

const initSelectedFilters = (): SelectedFilter<OrderDraftFilterKeys>[] => {
  const {query} = router.currentRoute.value;
  return FILTER_KEYS.filter((key) => query[key]).map((key) => ({
    key,
    value: query[key] as string,
  }));
};

export const useOrderDraftsState = defineStore(ORDER_DRAFTS_STORE_ID, {
  state: (): State => ({
    filters: {page: 1},
    selectedFilters: initSelectedFilters(),
    availableFilters: [
      {
        key: 'store',
        label: i18n.global.t('order.drafts.filters.store'),
        type: 'select',
        options: useWorkspaceStore().stores.map((s) => ({value: s.id, label: s.name})),
      },
      {
        key: 'fromDate',
        label: i18n.global.t('order.drafts.filters.fromDate'),
        type: 'date',
      },
      {
        key: 'toDate',
        label: i18n.global.t('order.drafts.filters.toDate'),
        type: 'date',
      },
    ],
    drafts: [],
    draft: undefined,
    count: 0,
  }),
  getters: {
    getFilters: (state) => (): GetManyOrderQuery => {
      const store = state.selectedFilters.find((f) => f.key === 'store');
      const fromDate = state.selectedFilters.find((f) => f.key === 'fromDate');
      const toDate = state.selectedFilters.find((f) => f.key === 'toDate');

      return {
        ...state.filters,
        ...(store && {store: store.value as string}),
        ...(fromDate && {fromDate: fromDate.value as string}),
        ...(toDate && {toDate: toDate.value as string}),
      };
    },
  },
  actions: {
    setPage(page: number) {
      this.filters.page = page;
    },
    select(draft?: OrderDraftResponse) {
      this.draft = draft;
    },
    applyFilter(key: OrderDraftFilterKeys, value: string | number) {
      const index = this.selectedFilters.findIndex((f) => f.key === key);

      const query = router.currentRoute.value.query;
      router.push({query: {...query, [key]: value}});

      if (index !== -1) {
        this.selectedFilters[index] = {key, value};
      } else {
        this.selectedFilters.push({key, value});
      }

      this.setPage(1);
      this.getMany();
    },
    resetFilter(key: OrderDraftFilterKeys) {
      this.selectedFilters = this.selectedFilters.filter((f) => f.key !== key);
      router.replace({query: {...router.currentRoute.value.query, [key]: undefined}});
      this.setPage(1);
      this.getMany();
    },
    async getMany() {
      const app = useAppStore();
      try {
        app.loading = true;
        const response = await sdk.wlapi.getOrderDrafts(this.getFilters());
        this.drafts = response.drafts;
        this.count = response.count;
      } catch (err) {
        formatError(err);
      } finally {
        app.loading = false;
      }
    },
    async searchById(draftId: string) {
      const app = useAppStore();
      try {
        app.loading = true;
        const draft = await sdk.wlapi.getOrderDraft(draftId);
        this.drafts = [draft];
        this.count = 1;
      } catch (err) {
        formatError(err);
      } finally {
        app.loading = false;
      }
    },
    async getById(draftId: string): Promise<OrderDraftResponse | undefined> {
      const app = useAppStore();
      try {
        app.loading = true;
        return await sdk.wlapi.getOrderDraft(draftId);
      } catch (err) {
        formatError(err);
      } finally {
        app.loading = false;
      }
    },
    async remove(draftId: string) {
      const app = useAppStore();
      try {
        app.loading = true;
        await sdk.wlapi.deleteOrderDraft(draftId);
        if (this.draft?._id === draftId) {
          this.draft = undefined;
        }
        return true;
      } catch (err) {
        formatError(err);
        return false;
      } finally {
        app.loading = false;
      }
    },
    async createDraft(data: Partial<CheckoutExternalStore>) {
      const app = useAppStore();
      try {
        app.loading = true;
        await sdk.wlapi.saveOrderDraft(data);
        return true;
      } catch (err) {
        formatError(err);
        return false;
      } finally {
        app.loading = false;
      }
    },
    async updateDraft(draftId: string, data: Partial<CheckoutExternalStore>) {
      const app = useAppStore();
      try {
        app.loading = true;
        this.draft = await sdk.wlapi.updateOrderDraft(draftId, data);
        return true;
      } catch (err) {
        formatError(err);
        return false;
      } finally {
        app.loading = false;
      }
    },
    async downloadQuotation(draftId: string) {
      const app = useAppStore();
      try {
        app.loading = true;
        const pdf = await sdk.wlapi.getOrderDraftPDFQuotation(draftId);
        const url = URL.createObjectURL(new Blob([pdf], {type: 'application/pdf'}));
        const link = document.createElement('a');
        link.href = url;
        link.download = `preventivo-${draftId}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        return true;
      } catch (err) {
        formatError(err);
        return false;
      } finally {
        app.loading = false;
      }
    },
    async confirmDraft(draftId: string, data: Partial<CheckoutExternalStore>) {
      const app = useAppStore();
      try {
        app.loading = true;
        const response: CheckoutResponse = await sdk.wlapi.confirmOrderDraft(draftId, data);
        this.draft = undefined;
        return response;
      } catch (err) {
        formatError(err);
        return false;
      } finally {
        app.loading = false;
      }
    },
  },
});
